import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useFriends } from '../api/hooks';
import { useAuth } from '../api/useAuth';
import { Stack, Row, Text, Input, Button, Panel, Avatar, Divider, PlattsPicker, Tooltip } from '../ui';

const SECTORS = ['Oil & Gas','LNG','Energy','Power','Carbon','Mining','Soft Commodities','Banking','Capital Markets','Tech','Infrastructure','Sovereign Capital','Other'];
const TYPES = ['Brokerage','Mandate','Equity co-invest','Advisory','Trade','Offtake','Other'];
const VOLUME_UNITS = [
  { v: 'bbl',   l: 'Barrels (bbl)' },
  { v: 'MT',    l: 'Metric tons (MT)' },
  { v: 'gal',   l: 'Gallons (gal)' },
  { v: 'mmbtu', l: 'MMBtu' },
  { v: 'mwh',   l: 'MWh' },
  { v: 'units', l: 'Units' },
];
const FREQUENCIES = [
  { v: 'one-off',   l: 'One-off (single shipment)' },
  { v: 'daily',     l: 'Daily' },
  { v: 'weekly',    l: 'Weekly' },
  { v: 'monthly',   l: 'Monthly' },
  { v: 'quarterly', l: 'Quarterly' },
  { v: 'annually',  l: 'Annually' },
];
const STATUSES = [
  { v: 'opportunity',     l: 'Opportunity',     hint: 'sourced, not yet pursued' },
  { v: 'active',          l: 'In progress',     hint: 'kyc, dd, negotiating' },
  { v: 'contract_signed', l: 'Contract signed', hint: 'binding agreement in hand' },
  { v: 'delivering',      l: 'Delivering',      hint: 'shipments / payments under way' },
  { v: 'completed',       l: 'Completed',       hint: 'closed and settled' },
  { v: 'cancelled',       l: 'Cancelled',       hint: 'killed or fell through' },
];
const PLATTS_BENCHMARKS = [
  'Dated Brent','ICE Brent','WTI Cushing','WTI Midland','Bonny Light','Forties','Mars',
  'Dubai','Oman','Murban','Urals',
  'JKM','TTF','NBP','Henry Hub','PVB',
  'Singapore Gasoil 10ppm','Singapore Jet','NYH RBOB','NWE Eurobob',
  'Singapore HSFO 380','NWE LSFO 0.5%',
  'Argus Premium Low Vol Coal','API2','API4','API6',
  'Other',
];

type Participant = { friendId: string; role: 'lead'|'contributor'|'silent'|'observer'; splitBp: number };
type PriceLeg = {
  basis: 'fixed' | 'platts';
  fixed: string;
  benchmark: string;
  code: string;
  benchmarkPrice: string;
  differential: string;
};

const ERR_RING = 'border-negative !border-2';

const fmt = (n: number, d = 0) => new Intl.NumberFormat('en-US', { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);
const fmt2 = (n: number) => fmt(n, 2);

function legEffective(leg: PriceLeg) {
  if (leg.basis === 'fixed') return parseFloat(leg.fixed || '0');
  return parseFloat(leg.benchmarkPrice || '0') + parseFloat(leg.differential || '0');
}

export function DealNew() {
  const nav = useNavigate();
  const { friend } = useAuth();
  const { data: friends } = useFriends();

  const [name, setName] = useState('');
  const [type, setType] = useState(TYPES[0]);
  const [sector, setSector] = useState(SECTORS[0]);
  const [geography, setGeography] = useState('');
  const [productGrade, setProductGrade] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [dealStatus, setDealStatus] = useState('opportunity');
  const isOG = sector === 'Oil & Gas' || sector === 'LNG' || sector === 'Energy';

  const [volumeAmount, setVolumeAmount] = useState('');
  const [volumeUnit, setVolumeUnit] = useState('bbl');
  const [contractFrequency, setContractFrequency] = useState('one-off');
  const [contractDurationMonths, setContractDurationMonths] = useState('');
  const [manualTicketSize, setManualTicketSize] = useState('');
  const [showManualTicket, setShowManualTicket] = useState(false);
  const [touched, setTouched] = useState({ name: false, ticket: false, splits: false, parties: false });

  const [buyLeg, setBuyLeg] = useState<PriceLeg>({ basis: 'fixed', fixed: '', benchmark: '', code: '', benchmarkPrice: '', differential: '' });
  const [sellLeg, setSellLeg] = useState<PriceLeg>({ basis: 'fixed', fixed: '', benchmark: '', code: '', benchmarkPrice: '', differential: '' });

  const [participants, setParticipants] = useState<Participant[]>(friend ? [{ friendId: friend.id, role: 'lead', splitBp: 10000 }] : []);
  const [pendingParties, setPendingParties] = useState<Array<{
    role: 'buyer'|'seller'|'counterparty'|'guarantor'|'agent';
    legalName: string; companyName?: string;
    contactName?: string; contactEmail?: string; contactPhone?: string;
    contactTitle?: string; jurisdiction?: string; registrationId?: string;
    address?: string; notes?: string;
  }>>([]);
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState<string | null>(null);

  // Live derived values
  const vol = parseFloat(volumeAmount || '0');
  const months = parseInt(contractDurationMonths || '0', 10);
  const buyPx = legEffective(buyLeg);
  const sellPx = legEffective(sellLeg);

  // Spread-locked mode: both legs platts-indexed to the SAME benchmark with differentials filled.
  // Profit = sell_diff − buy_diff regardless of benchmark print. This is the standard O&G workflow:
  // you lock the spread at deal sign-off; the actual Brent/WTI print on delivery day is irrelevant
  // to your spread P&L. Volume × spread = ticket size you can legitimately commit to.
  const sellDiff = parseFloat(sellLeg.differential || '0');
  const buyDiff = parseFloat(buyLeg.differential || '0');
  const sellHasDiff = sellLeg.basis === 'platts' && sellLeg.differential !== '';
  const buyHasDiff = buyLeg.basis === 'platts' && buyLeg.differential !== '';
  const sameBenchmark =
    sellLeg.basis === 'platts' && buyLeg.basis === 'platts' &&
    !!sellLeg.benchmark && !!buyLeg.benchmark &&
    sellLeg.benchmark.trim().toLowerCase() === buyLeg.benchmark.trim().toLowerCase();
  const spreadLocked = sameBenchmark && sellHasDiff && buyHasDiff;
  const lockedSpread = spreadLocked ? (sellDiff - buyDiff) : 0;

  // Effective spread used for ticket math: prefer absolute (sell − buy) when both prices known,
  // otherwise fall back to locked differential spread.
  const spread = (sellPx > 0 && buyPx > 0) ? (sellPx - buyPx) : (spreadLocked ? lockedSpread : 0);

  // Revenue/cost can only be computed when actual price levels are known.
  // If only spread is known (typical at deal-sign), gross = vol × spread.
  const revPerShip = vol * sellPx;
  const costPerShip = vol * buyPx;
  const grossPerShip = (sellPx > 0 && buyPx > 0) ? (vol * (sellPx - buyPx)) : (spreadLocked ? vol * lockedSpread : 0);
  const marginPct = revPerShip > 0 ? (grossPerShip / revPerShip) * 100 : 0;
  const SHIPS_YR: any = { 'one-off': 1, 'daily': 365, 'weekly': 52, 'monthly': 12, 'quarterly': 4, 'annually': 1 };
  const shipsYr = SHIPS_YR[contractFrequency] ?? 0;
  const annualGross = grossPerShip * shipsYr;
  const annualRev   = revPerShip   * shipsYr;
  const annualCost  = costPerShip  * shipsYr;

  // Ticket size: use revenue if both prices known, otherwise use gross-spread × period.
  // This lets a deal be created with only the spread locked.
  const periodGross = contractFrequency === 'one-off' ? grossPerShip : (months > 0 ? (annualGross / 12) * months : annualGross);
  const periodRev   = contractFrequency === 'one-off' ? revPerShip   : (months > 0 ? (annualRev   / 12) * months : annualRev);
  const computedTicketSize = periodRev > 0 ? periodRev : periodGross;
  const manualTs = parseFloat(manualTicketSize || '0');
  const ticketSize = manualTs > 0 ? manualTs : computedTicketSize;

  const safe = (n: number) => Number.isFinite(n) && n >= 0 ? n : 0;
  const isVolumeBased = vol > 0 && (Math.abs(sellPx) > 0 || Math.abs(buyPx) > 0 || spreadLocked);

  function setParticipant(i: number, patch: Partial<Participant>) {
    setParticipants(prev => prev.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  }
  function addParticipant() {
    const remaining = friends.find((f: any) => !participants.some(p => p.friendId === f.id));
    if (!remaining) return;
    setParticipants(prev => [...prev, { friendId: remaining.id, role: 'contributor', splitBp: 0 }]);
  }
  function removeParticipant(i: number) {
    setParticipants(prev => prev.filter((_, idx) => idx !== i));
  }

  const totalBp = participants.reduce((a, p) => a + p.splitBp, 0);

  // Structured validation — collect all issues, surface inline + on tooltip
  const issues: string[] = [];
  if (!name.trim()) issues.push('deal needs a name');

  // Diagnose ticket-size — point at the actual missing field.
  // Spread-locked deals (both legs platts-indexed to same benchmark, both differentials set) pass
  // even if benchmark price is empty: gross = volume × locked spread.
  if (ticketSize <= 0) {
    if (manualTs > 0) {
      // user typed a manual ticket size — should be > 0; this branch shouldn't hit
    } else if (vol === 0 && manualTs === 0) {
      issues.push('set a manual ticket size, or enter volume plus buy and sell pricing below');
    } else {
      // volume entered but ticket = 0 → diagnose which side is incomplete
      const sellEmpty = (sellLeg.basis === 'fixed' && sellLeg.fixed === '') ||
                       (sellLeg.basis === 'platts' && sellLeg.differential === '');
      const buyEmpty = (buyLeg.basis === 'fixed' && buyLeg.fixed === '') ||
                       (buyLeg.basis === 'platts' && buyLeg.differential === '');

      if (sellEmpty && buyEmpty) {
        issues.push('enter sell and buy pricing (or just the differentials if both legs index the same benchmark)');
      } else if (sellEmpty) {
        if (sellLeg.basis === 'fixed') issues.push('enter a sell price per ' + volumeUnit);
        else issues.push('enter the sell-side differential in ' + currency + '/' + volumeUnit + ' (e.g. +1.20)');
      } else if (buyEmpty) {
        if (buyLeg.basis === 'fixed') issues.push('enter a buy price per ' + volumeUnit);
        else issues.push('enter the buy-side differential in ' + currency + '/' + volumeUnit + ' (e.g. -0.50)');
      } else if (sellLeg.basis === 'platts' && buyLeg.basis === 'platts' && !sameBenchmark) {
        issues.push('legs index different benchmarks (' + sellLeg.benchmark + ' vs ' + buyLeg.benchmark + ') — fill in benchmark prices on both legs to compute spread');
      } else {
        issues.push('check pricing inputs — spread is zero or negative');
      }
    }
  }

  if (totalBp !== 10000) issues.push(`participant splits must total 100% (currently ${(totalBp/100).toFixed(0)}%)`);
  if (!participants.some(p => p.role === 'lead')) issues.push('one participant must be the lead');
  pendingParties.forEach((p, i) => {
    if (!p.legalName?.trim()) issues.push(`party #${i+1} (${p.role}) needs a legal name`);
  });

  const valid = issues.length === 0;

  // Per-field error flags for inline highlighting
  const err = {
    name: !name.trim() && touched.name,
    ticket: ticketSize <= 0 && touched.ticket,
    splits: totalBp !== 10000 && touched.splits,
    lead: !participants.some(p => p.role === 'lead'),
  };

  async function submit() {
    if (!valid) return;
    setBusy(true); setSubmitErr(null);
    try {
      const body: any = {
        name: name.trim(), type, sector, geography: geography || undefined,
        productGrade: productGrade || undefined,
        ticketSize: ticketSize || revPerShip || 1,
        currency,
        expectedRevenue: safe(annualRev) || safe(revPerShip) || 0,
        expectedMargin: revPerShip > 0 ? grossPerShip / revPerShip : 0,
        dealStatus,
        participants,
      };
      if (isVolumeBased) {
        body.volumeAmount = vol;
        body.volumeUnit = volumeUnit;
        body.contractFrequency = contractFrequency;
        if (months > 0) body.contractDurationMonths = months;

        // SELL leg
        body.sellPricingBasis = sellLeg.basis;
        if (sellLeg.basis === 'fixed') body.sellPricePerUnit = parseFloat(sellLeg.fixed) || 0;
        else {
          body.sellPlattsBenchmark = sellLeg.benchmark;
          body.sellPlattsCode = sellLeg.code || undefined;
          body.sellPlattsBenchmarkPrice = parseFloat(sellLeg.benchmarkPrice) || 0;
          body.sellPlattsDifferential = parseFloat(sellLeg.differential) || 0;
        }
        // Legacy mirror: pricePerUnit so old reads still work
        body.pricePerUnit = safe(sellPx);

        // BUY leg
        body.buyPricingBasis = buyLeg.basis;
        if (buyLeg.basis === 'fixed') body.buyPricePerUnit = parseFloat(buyLeg.fixed) || 0;
        else {
          body.buyPlattsBenchmark = buyLeg.benchmark;
          body.buyPlattsCode = buyLeg.code || undefined;
          body.buyPlattsBenchmarkPrice = parseFloat(buyLeg.benchmarkPrice) || 0;
          body.buyPlattsDifferential = parseFloat(buyLeg.differential) || 0;
        }
        // Legacy mirror: costPerUnit
        body.costPerUnit = safe(buyPx);
      }
      const r = await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/deals`, {
        method: 'POST', credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error(await r.text());
      const { id } = await r.json();
      // Create any pending parties
      for (const p of pendingParties) {
        try {
          await fetch(`${import.meta.env.VITE_API_URL || 'https://api.ardacious.app'}/parties/deal/${id}`, {
            method: 'POST', credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify(p),
          });
        } catch {}
      }
      nav(`/deals/${id}`);
    } catch (e: any) { setSubmitErr(e.message); } finally { setBusy(false); }
  }


  return (
    <Stack gap={3}>
      <Link to="/deals" className="text-xs text-muted hover:text-ink">← deals</Link>
      <Text variant="display">New deal</Text>

      <Panel title="Status">
        <Row gap={1} wrap>
          {STATUSES.map(s => {
            const on = dealStatus === s.v;
            return (
              <button key={s.v} onClick={() => setDealStatus(s.v)}
                className={`text-left h-auto px-2 py-2 rounded-sm hairline transition-colors ${on ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
                <div className="text-sm font-medium">{s.l}</div>
                <div className="text-[10px] opacity-70">{s.hint}</div>
              </button>
            );
          })}
        </Row>
      </Panel>

      <Panel title="Basics">
        <Stack gap={2}>
          <Stack gap={0.5}><Text variant="label">name</Text><Input value={name} onChange={e => setName(e.target.value)} onBlur={() => setTouched(t => ({ ...t, name: true }))} placeholder="e.g. Bonny Light → Rotterdam Q3" className={err.name ? ERR_RING : ""} /></Stack>
          <Row gap={2}>
            <Stack gap={0.5} className="flex-1"><Text variant="label">type</Text>
              <select value={type} onChange={e => setType(e.target.value)} className="h-9 hairline rounded-sm px-2 bg-bg text-base">
                {TYPES.map(t => <option key={t}>{t}</option>)}
              </select></Stack>
            <Stack gap={0.5} className="flex-1"><Text variant="label">sector</Text>
              <select value={sector} onChange={e => setSector(e.target.value)} className="h-9 hairline rounded-sm px-2 bg-bg text-base">
                {SECTORS.map(t => <option key={t}>{t}</option>)}
              </select></Stack>
          </Row>
          <Row gap={2}>
            <Stack gap={0.5} className="flex-1"><Text variant="label">geography</Text><Input value={geography} onChange={e => setGeography(e.target.value)} placeholder="e.g. West Africa → NW Europe" /></Stack>
            {isOG && <Stack gap={0.5} className="flex-1"><Text variant="label">product / grade</Text><Input value={productGrade} onChange={e => setProductGrade(e.target.value)} placeholder="e.g. Bonny Light, WTI, JKM" /></Stack>}
          </Row>
        </Stack>
      </Panel>

      <Panel title="Ticket size">
        <Stack gap={2}>
          <Row gap={2} wrap align="start">
            <Stack gap={0.5} className="flex-1 min-w-[220px]">
              <Text variant="label">computed ticket size</Text>
              {computedTicketSize > 0 ? (
                <Stack gap={0.5}>
                  <div className="text-xl tnum font-medium text-ink">{currency} {fmt(computedTicketSize)}</div>
                  <Text variant="micro" muted>
                    {(sellPx > 0 && buyPx > 0)
                      ? `from ${fmt(vol)} ${volumeUnit} × ${currency} ${fmt2(sellPx)}/${volumeUnit}${contractFrequency !== 'one-off' ? ` × ${months > 0 ? `${months} mo` : 'annual'}` : ''}`
                      : spreadLocked
                        ? `from ${fmt(vol)} ${volumeUnit} × locked spread ${currency} ${fmt2(lockedSpread)}/${volumeUnit}${contractFrequency !== 'one-off' ? ` × ${months > 0 ? `${months} mo` : 'annual'}` : ''}`
                        : 'derived from volume and pricing below'}
                  </Text>
                </Stack>
              ) : (
                <Stack gap={0.5}>
                  <div className="text-xl tnum font-medium text-muted">{currency} —</div>
                  <Text variant="micro" muted>
                    {vol === 0
                      ? 'enter volume and at least one price (or differential) below to auto-compute'
                      : 'add sell pricing below — or click set custom amount if this is a non-volume deal'}
                  </Text>
                </Stack>
              )}
              {!showManualTicket && (
                <button
                  type="button"
                  onClick={() => setShowManualTicket(true)}
                  className="text-xs text-muted hover:text-ink underline self-start tnum"
                >
                  {computedTicketSize > 0 ? 'override with custom amount' : 'set custom amount (advisory / mandate / fee deals)'}
                </button>
              )}
              {showManualTicket && (
                <Stack gap={0.5} className="hairline rounded-sm p-2 bg-whisper">
                  <Row gap={1} align="center" justify="between">
                    <Text variant="label">custom ticket size ({currency}){manualTs > 0 ? ' — overrides auto' : ''}</Text>
                    <button
                      type="button"
                      onClick={() => { setShowManualTicket(false); setManualTicketSize(''); }}
                      className="text-xs text-muted hover:text-ink underline tnum"
                    >clear</button>
                  </Row>
                  <Input
                    type="number"
                    value={manualTicketSize}
                    onChange={e => setManualTicketSize(e.target.value)}
                    onBlur={() => setTouched(t => ({ ...t, ticket: true }))}
                    placeholder="0"
                    className={err.ticket ? ERR_RING : ''}
                  />
                </Stack>
              )}
              {ticketSize > 0 && manualTs > 0 && computedTicketSize > 0 && (
                <Text variant="micro" muted>using custom amount; auto would be {currency} {fmt(computedTicketSize)}</Text>
              )}
            </Stack>
            <Stack gap={0.5} className="w-28 sm:w-24 shrink-0">
              <Text variant="label">currency</Text>
              <select value={currency} onChange={e => setCurrency(e.target.value)} className="h-10 sm:h-9 hairline rounded-sm px-2 bg-bg text-sm">
                {['USD','EUR','GBP','JPY','SGD','AED','CHF','BRL'].map(c => <option key={c}>{c}</option>)}
              </select>
            </Stack>
          </Row>
        </Stack>
      </Panel>

      <Panel title="Volume & contract (oil & gas / commodity deals)">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <Stack gap={0.5}>
            <Text variant="label">volume per shipment</Text>
            <Input type="number" value={volumeAmount} onChange={e => setVolumeAmount(e.target.value)} placeholder="0" />
          </Stack>
          <Stack gap={0.5}>
            <Text variant="label">unit</Text>
            <select value={volumeUnit} onChange={e => setVolumeUnit(e.target.value)} className="h-10 sm:h-9 hairline rounded-sm px-2 bg-bg text-sm">
              {VOLUME_UNITS.map(u => <option key={u.v} value={u.v}>{u.l}</option>)}
            </select>
          </Stack>
          <Stack gap={0.5}>
            <Text variant="label">delivery frequency</Text>
            <select value={contractFrequency} onChange={e => setContractFrequency(e.target.value)} className="h-10 sm:h-9 hairline rounded-sm px-2 bg-bg text-sm">
              {FREQUENCIES.map(f => <option key={f.v} value={f.v}>{f.l}</option>)}
            </select>
          </Stack>
          <Stack gap={0.5}>
            <Text variant="label">contract length (months)</Text>
            <Input type="number" value={contractDurationMonths} onChange={e => setContractDurationMonths(e.target.value)} placeholder="0 if one-off" />
          </Stack>
        </div>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PriceLegEditor title="What we buy it for" leg={buyLeg} setLeg={setBuyLeg} unit={volumeUnit} currency={currency} effective={buyPx} />
        <PriceLegEditor title="What we sell it for" leg={sellLeg} setLeg={setSellLeg} unit={volumeUnit} currency={currency} effective={sellPx} />
      </div>

      {spreadLocked && (
        <div className="hairline rounded-sm p-3 bg-positive-soft border-positive">
          <Row gap={2} align="center" justify="between">
            <Stack gap={0.5}>
              <Text variant="small" className="font-medium">spread locked at sign-off</Text>
              <Text variant="micro" muted>both legs index {sellLeg.benchmark}. profit equals sell-diff minus buy-diff regardless of {sellLeg.benchmark} print on delivery day.</Text>
            </Stack>
            <Stack gap={0.5} className="text-right">
              <Text variant="micro" muted>locked spread</Text>
              <Text variant="body" className="tnum font-medium">{lockedSpread >= 0 ? '+' : ''}{currency} {fmt2(lockedSpread)} / {volumeUnit}</Text>
              {vol > 0 && (
                <Text variant="micro" muted>× {fmt(vol)} {volumeUnit} = {currency} {fmt(vol * lockedSpread)} per shipment</Text>
              )}
            </Stack>
          </Row>
        </div>
      )}

      {sellLeg.basis === 'platts' && buyLeg.basis === 'platts' && sellLeg.benchmark && buyLeg.benchmark && !sameBenchmark && (
        <div className="hairline rounded-sm p-3 bg-warning-soft border-warning">
          <Stack gap={0.5}>
            <Text variant="small" className="font-medium">legs index different benchmarks</Text>
            <Text variant="micro" muted>sell ({sellLeg.benchmark}) and buy ({buyLeg.benchmark}) can't be netted as a pure differential spread. Fill in benchmark prices on both legs to compute the cross-index spread.</Text>
          </Stack>
        </div>
      )}

      {isVolumeBased && (
        <Panel title="Spread → profit">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KV label="profit per unit" value={`${currency} ${fmt2(spread)}/${volumeUnit}`} accent />
            <KV label="profit per shipment" value={`${currency} ${fmt(grossPerShip)}`} accent />
            <KV label="margin %" value={`${fmt(marginPct, 2)}%`} />
            <KV label="annual gross" value={`${currency} ${fmt(annualGross)}`} accent />
          </div>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <KV label="weekly" value={`${currency} ${fmt(annualGross / 52)}`} />
            <KV label="monthly" value={`${currency} ${fmt(annualGross / 12)}`} />
            <KV label="annual" value={`${currency} ${fmt(annualGross)}`} />
          </div>
          {months > 0 && contractFrequency !== 'one-off' && (
            <div className="mt-2"><Text variant="label">contract total over {months} months: <span className="tnum">{currency} {fmt((annualGross / 12) * months)}</span> gross</Text></div>
          )}
        </Panel>
      )}

      
      <Panel title="Buyer & Seller (and other counterparties)">
        <Stack gap={2}>
          <Text variant="small" muted>capture the legal entities and contact info on each side. they appear on the deal page; you can issue secure portal links from there.</Text>
          {pendingParties.length === 0 && <Text variant="label" muted>no parties added yet</Text>}
          {pendingParties.map((p, i) => (
            <div key={i} className="hairline rounded-sm p-3 bg-whisper">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Stack gap={0.5}>
                  <Text variant="label">role</Text>
                  <select value={p.role} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, role: e.target.value as any } : x))} className="h-10 sm:h-9 hairline rounded-sm px-2 bg-bg text-sm">
                    <option value="buyer">buyer</option>
                    <option value="seller">seller</option>
                    <option value="counterparty">counterparty</option>
                    <option value="guarantor">guarantor</option>
                    <option value="agent">agent / broker</option>
                  </select>
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">legal name *</Text>
                  <Input value={p.legalName} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, legalName: e.target.value } : x))} placeholder="e.g. Atlantic Trader BV" />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">trading / d.b.a. name</Text>
                  <Input value={p.companyName ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, companyName: e.target.value } : x))} />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">jurisdiction</Text>
                  <Input value={p.jurisdiction ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, jurisdiction: e.target.value } : x))} placeholder="NL, NG, SG…" />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">reg / VAT id</Text>
                  <Input value={p.registrationId ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, registrationId: e.target.value } : x))} />
                </Stack>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                <Stack gap={0.5}>
                  <Text variant="label">contact name</Text>
                  <Input value={p.contactName ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, contactName: e.target.value } : x))} />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">contact email</Text>
                  <Input type="email" value={p.contactEmail ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, contactEmail: e.target.value } : x))} />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">phone</Text>
                  <Input value={p.contactPhone ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, contactPhone: e.target.value } : x))} />
                </Stack>
                <Stack gap={0.5}>
                  <Text variant="label">title</Text>
                  <Input value={p.contactTitle ?? ''} onChange={e => setPendingParties(prev => prev.map((x, idx) => idx === i ? { ...x, contactTitle: e.target.value } : x))} />
                </Stack>
              </div>
              <div className="mt-2">
                <Button variant="ghost" size="sm" onClick={() => setPendingParties(prev => prev.filter((_, idx) => idx !== i))}>remove</Button>
              </div>
            </div>
          ))}
          <Row gap={1}>
            <Button variant="secondary" size="sm" onClick={() => setPendingParties(prev => [...prev, { role: 'buyer', legalName: '' }])}>+ add buyer</Button>
            <Button variant="secondary" size="sm" onClick={() => setPendingParties(prev => [...prev, { role: 'seller', legalName: '' }])}>+ add seller</Button>
            <Button variant="ghost" size="sm" onClick={() => setPendingParties(prev => [...prev, { role: 'counterparty', legalName: '' }])}>+ other party</Button>
          </Row>
        </Stack>
      </Panel>

      <Panel title={`Participants — splits sum to ${(totalBp/100).toFixed(0)}%${err.splits ? ' (must be 100%)' : ''}`}>
        <Stack gap={2}>
          {participants.map((p, i) => {
            const f = friends.find((x: any) => x.id === p.friendId);
            const portion = isVolumeBased ? annualGross * (p.splitBp / 10000) : 0;
            return (
              <Row key={i} gap={2} align="center">
                {f && <><Avatar name={f.name} friendId={f.id} hasAvatar={!!f.avatarUrl} size="sm" /><Text variant="small" className="w-32 truncate">{f.name}</Text></>}
                <select value={p.role} onChange={e => setParticipant(i, { role: e.target.value as any })} className="h-7 hairline rounded-sm px-1 bg-bg text-xs">
                  <option value="lead">lead</option><option value="contributor">contributor</option><option value="silent">silent</option><option value="observer">observer</option>
                </select>
                <Input type="number" value={p.splitBp / 100} onChange={e => setParticipant(i, { splitBp: Math.round(parseFloat(e.target.value || '0') * 100) })} className="w-20" />
                <span className="text-xs text-muted">%</span>
                {portion > 0 && <Text variant="label" className="tnum">{currency} {fmt(portion)} / yr</Text>}
                <Button variant="ghost" size="sm" onClick={() => removeParticipant(i)}>remove</Button>
              </Row>
            );
          })}
          <Row gap={1}>
            <Button variant="secondary" size="sm" onClick={addParticipant}>+ add participant</Button>
            <Text variant="label">{totalBp === 10000 ? '✓ splits balanced' : `splits must total 100% (currently ${(totalBp/100).toFixed(0)}%)`}</Text>
          </Row>
        </Stack>
      </Panel>

      {/* Show all open issues as a list + the actual button */}
      {submitErr && <Text variant="small" className="text-negative">{submitErr}</Text>}
      {issues.length > 0 && (
        <div className="hairline rounded-sm bg-negative/5 border-negative/30 p-2">
          <Text variant="micro" className="text-negative">fix these to enable submit</Text>
          <ul className="mt-1 list-disc list-inside text-xs text-ink">
            {issues.map((it, i) => <li key={i}>{it}</li>)}
          </ul>
        </div>
      )}
      <Row gap={1}>
        {valid ? (
          <Button variant="primary" disabled={busy} onClick={submit}>{busy ? 'creating…' : 'create deal'}</Button>
        ) : (
          <Tooltip content={`Can't submit yet:\n• ${issues.join('\n• ')}`}>
            <Button variant="primary" disabled onClick={() => {
              // Mark every field touched so inline errors light up
              setTouched({ name: true, ticket: true, splits: true, parties: true });
            }}>create deal</Button>
          </Tooltip>
        )}
        <Link to="/deals"><Button variant="ghost">cancel</Button></Link>
      </Row>
    </Stack>
  );
}

function PriceLegEditor({ title, leg, setLeg, unit, currency, effective }: { title: string; leg: PriceLeg; setLeg: (l: PriceLeg) => void; unit: string; currency: string; effective: number }) {
  const eff = legEffective(leg);
  return (
    <Panel title={title}>
      <Stack gap={2}>
        <Row gap={1}>
          <button onClick={() => setLeg({ ...leg, basis: 'fixed' })} className={`flex-1 text-left px-2 py-2 rounded-sm hairline transition-colors ${leg.basis === 'fixed' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
            <div className="text-sm font-medium">fixed</div>
            <div className="text-[10px] opacity-70">flat price / {unit}</div>
          </button>
          <button onClick={() => setLeg({ ...leg, basis: 'platts' })} className={`flex-1 text-left px-2 py-2 rounded-sm hairline transition-colors ${leg.basis === 'platts' ? 'bg-ink text-bg border-ink' : 'text-muted hover:text-ink'}`}>
            <div className="text-sm font-medium">platts +/-</div>
            <div className="text-[10px] opacity-70">benchmark + differential</div>
          </button>
        </Row>

        {leg.basis === 'fixed' ? (
          <Stack gap={0.5}>
            <Text variant="label">price / {unit}</Text>
            <Input type="number" step="0.01" value={leg.fixed} onChange={e => setLeg({ ...leg, fixed: e.target.value })} placeholder="e.g. 82.50" />
          </Stack>
        ) : (
          <Stack gap={2}>
            <PlattsPicker
              benchmark={leg.benchmark}
              code={(leg as any).code ?? ''}
              onChange={({ benchmark, code }) => setLeg({ ...leg, benchmark, code } as any)}
            />
            <Row gap={1}>
              <Stack gap={0.5} className="flex-1">
                <Row gap={1} align="center" justify="between">
                  <Text variant="label">benchmark price ({currency}/{unit})</Text>
                  <Text variant="micro" muted>optional at sign-off</Text>
                </Row>
                <Input type="number" step="0.01" value={leg.benchmarkPrice} onChange={e => setLeg({ ...leg, benchmarkPrice: e.target.value })} placeholder="e.g. 82.50 (or leave blank)" />
                <Text variant="micro" muted>printed on delivery day. Leave blank if you only want to lock the spread now.</Text>
              </Stack>
              <Stack gap={0.5} className="flex-1">
                <Row gap={1} align="center" justify="between">
                  <Text variant="label">+/- differential ({currency}/{unit})</Text>
                  <Text variant="micro" muted>not %</Text>
                </Row>
                <div className="relative">
                  <Input type="number" step="0.01" value={leg.differential} onChange={e => setLeg({ ...leg, differential: e.target.value })} placeholder="e.g. -0.50" className="pr-16" />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted tnum pointer-events-none">{currency}/{unit}</span>
                </div>
                <Text variant="micro" muted>dollars and cents per {unit}, e.g. +1.20 means +$1.20/{unit} above benchmark</Text>
              </Stack>
            </Row>
          </Stack>
        )}

        <Divider />
        <Row justify="between">
          <Text variant="micro">effective</Text>
          <Text variant="body" className="tnum font-medium">{currency} {fmt2(eff)} / {unit}</Text>
        </Row>
      </Stack>
    </Panel>
  );
}

function KV({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className={`hairline rounded-sm p-2 ${accent ? 'bg-whisper' : ''}`}>
      <Text variant="label">{label}</Text>
      <div className={`tnum text-sm ${accent ? 'font-medium' : ''}`}>{value}</div>
    </div>
  );
}
